import { getTableColumns } from "drizzle-orm";
import {
  integer,
  pgEnum,
  pgTable,
  text,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import z from "zod";

import { customTimestamp } from "./utils/types";

export const documentGroupEnumSchema = z.enum([
  "PROOF_OF_IDENTITY",
  "SOCIAL_SECURITY_NUMBER",
  "PROOF_OF_RESIDENCE",
  "PROOF_OF_INCOME_AND_EMPLOYMENT",
  // Displayed as "Proof of Immigration"; enum value kept for admin reporting/BigQuery stability.
  "PROOF_OF_CITIZENSHIP",
  "PROOF_OF_INSURANCE",
  "PROOF_OF_EXPENSE",
  "PROOF_OF_WORK",
  "PROOF_OF_EXEMPTION",
]);
export type DocumentGroupEnumType = z.infer<typeof documentGroupEnumSchema>;

export const applicationDocumentGroupEnumSchema =
  documentGroupEnumSchema.exclude(["PROOF_OF_WORK", "PROOF_OF_EXEMPTION"]);

export const documentGroupEnum = pgEnum(
  "document_group_enum",
  documentGroupEnumSchema.enum,
);

export const documentTypeEnumSchema = z.enum([
  "DRIVERS_LICENSE",
  "STATE_ID",
  "PASSPORT",
  "BIRTH_CERTIFICATE",
  "USCIS_ID",
  "AUTHORIZED_EMPLOYMENT_ID",
  "SOCIAL_SECURITY_CARD",
  "UTILITY_BILL",
  "RENT_RECEIPT_OR_LEASE_AGREEMENT",
  "MORTGAGE_STATEMENT",
  "PROPERTY_TAX_BILL",
  "PAY_STUBS",
  "BANK_STATEMENTS",
  "TAX_RETURN",
  "PENSION_STATEMENTS",
  "SSA_STATEMENTS",
  "SSDI_STATEMENTS",
  "COURT_ORDERED_STATEMENTS",
  "UNEMPLOYMENT_STATEMENTS",
  "LETTER_OF_STATEMENT_FROM_EMPLOYER",
  "IMIGRATION_DOCUMENTS_AND_APPROVALS",
  "I_20",
  "INSURANCE_POLICY_NUMBER",
  "INSURANCE_CARRIER_INFO",
  "INSURANCE_CARD",
  "STUDENT_LOANS",
  "TUITION_FEES",
  "ALIMONY_PAID",
  "MOVING_EXPENSES",
  "IRA_CONTRIBUTIONS",
  "CHILDCARE",
  "MEDICAL_EXPENSE",
  "EXPENSE_OTHER",
  "SIGNED_TIMESHEETS",
  "W2_FORMS",
  "SIGNED_LETTER",
  "SCHOOL_ENROLLMENT_VERIFICATION_OR_TRANSCRIPT",
  "TRAINING_OR_APPRENTICESHIP_ENROLLMENT_CERTIFICATE",
  "DOCTORS_NOTE",
  "CHILDS_BIRTH_CERTIFICATE",
  "CHILDS_PASSPORT",
  "GUARDIANSHIP_OR_CUSTODY_PAPERS",
  "TREATMENT_PROGRAM_ENROLLMENT_LETTER",
  "SNAP_TANF_VERIFICATION_LETTER",
  "FOSTER_CARE_VERIFICATION_LETTER",
  "TRIBAL_ENROLLMENT_CARD_OR_LETTER",
  "VA_DISABILITY_RATING_LETTER",
  "RELEASE_OR_DISCHARGE_PAPERWORK",
  "MEDICARE_CARD",
]);
export type DocumentTypeEnumType = z.infer<typeof documentTypeEnumSchema>;

export const documentCategoriesTable = pgTable(
  "document_categories",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    group: documentGroupEnum("category").notNull(),
    type: text("type").$type<DocumentTypeEnumType>().notNull(),
    displayOrder: integer("display_order").notNull(),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    unique("document_categories_group_type_unique_idx").on(
      table.group,
      table.type,
    ),
    unique("document_categories_group_display_order_unique_idx").on(
      table.group,
      table.displayOrder,
    ),
  ],
);

export const documentCategoriesTableColumns = getTableColumns(
  documentCategoriesTable,
);

export const documentCategorySchema = createSelectSchema(
  documentCategoriesTable,
  {
    group: documentGroupEnumSchema,
    type: documentTypeEnumSchema,
  },
);
export type DocumentCategoryType = z.infer<typeof documentCategorySchema>;

export const documentCategoryInsertSchema = createInsertSchema(
  documentCategoriesTable,
  {
    group: documentGroupEnumSchema,
    type: documentTypeEnumSchema,
  },
).omit({
  createdAt: true,
  updatedAt: true,
});
export type DocumentCategoryInsertType = z.infer<
  typeof documentCategoryInsertSchema
>;
